import { useState, useEffect, useCallback, useMemo } from "react";
import { API_BASE_URL, authFetch } from "../config/api";
import { getCatalogue } from "../utils/offlineQuery";

const cache = { courses: null, departments: null, ts: 0 };
const CACHE_MS = 5 * 60 * 1000;

export function useCatalogue() {
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch_catalogue = useCallback(async () => {
    const now = Date.now();
    if (cache.courses && now - cache.ts < CACHE_MS) {
      setCourses(cache.courses);
      setDepartments(cache.departments);
      setLoading(false);
      return;
    }
    try {
      let c = [],
        d = [];

      // Try live API first, fall back to IndexedDB on any failure
      try {
        const [cRes, dRes] = await Promise.all([
          authFetch(`${API_BASE_URL}/api/catalogue/courses`),
          authFetch(`${API_BASE_URL}/api/catalogue/departments`),
        ]);
        if (!cRes.ok || !dRes.ok) throw new Error("API error");
        const cText = await cRes.text();
        const dText = await dRes.text();
        if (cText.trim().startsWith("<") || dText.trim().startsWith("<"))
          throw new Error("HTML response");
        const cJson = JSON.parse(cText);
        const dJson = JSON.parse(dText);
        c = cJson.courses || [];
        d = dJson.departments || [];
      } catch {
        // Fall back to IndexedDB
        const result = await getCatalogue();
        c = result.courses || [];
        d = result.departments || [];
      }

      if (c.length > 0) {
        cache.courses = c;
        cache.departments = d;
        cache.ts = now;
      }
      setCourses(c);
      setDepartments(d);
    } catch (err) {
      console.error("Failed to load catalogue", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch_catalogue();
  }, [fetch_catalogue]);

  const getCoursesForLevel = useCallback(
    (level, semester) => {
      const result = {};
      const lvl = Number(level);
      courses
        .filter(
          (c) =>
            c.type === "professional" &&
            c.level === lvl &&
            c.semester === Number(semester),
        )
        .forEach((c) => {
          result[c.code] = c.title;
        });
      return result;
    },
    [courses],
  );

  const getUnitLoad = useCallback(
    (code) => {
      const c = courses.find((c) => c.code === code?.toLowerCase());
      return c ? c.unit_load : null;
    },
    [courses],
  );

  const getExternalCourses = useCallback(() => {
    const result = {};
    courses
      .filter((c) => c.type === "external")
      .forEach((c) => {
        result[c.code] = c.title;
      });
    return result;
  }, [courses]);

  const getExternalForLevel = useCallback(
    (level, semester) => {
      const result = {};
      courses
        .filter(
          (c) =>
            c.type === "external" &&
            c.level === Number(level) &&
            c.semester === Number(semester),
        )
        .forEach((c) => {
          result[c.code] = c.title;
        });
      return result;
    },
    [courses],
  );

  const getCourseTitle = useCallback(
    (code) => {
      const c = courses.find((c) => c.code === code?.toLowerCase());
      return c ? c.title : code;
    },
    [courses],
  );

  const getDepartmentMap = useCallback(() => {
    const result = {};
    departments.forEach((d) => {
      result[d.code] = d.name;
    });
    return result;
  }, [departments]);

  const professionalCodes = useMemo(
    () =>
      new Set(
        courses.filter((c) => c.type === "professional").map((c) => c.code),
      ),
    [courses],
  );
  const isProfessional = useCallback(
    (code) => professionalCodes.has(code?.toLowerCase()),
    [professionalCodes],
  );

  return {
    courses,
    departments,
    loading,
    getCoursesForLevel,
    getUnitLoad,
    getExternalCourses,
    getExternalForLevel,
    getCourseTitle,
    getDepartmentMap,
    isProfessional,
    refetch: fetch_catalogue,
  };
}
